import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS, ROUTE_IDS } from '../routes/ids.js';
import { getRouteById } from '../routes/effective-routes.js';
const tk = (value) => value;
const fieldId = (value) => value;
function metadata(id) {
    return {
        id: id,
        schemaVersion: 1,
        module: MODULE_KEYS.TAX,
        feature: FEATURE_KEYS.ADMIN_TAX_RULES,
        privacyClassification: 'CONFIDENTIAL',
        telemetryId: `experience.taxRules.${id}`,
        accessibilityLabelKey: tk('investment.data.taxRules.title'),
        availability: 'AVAILABLE',
        owner: 'tax-rule',
    };
}
function field(name, type, required = false, options) {
    return {
        id: fieldId(`field.taxRule.${name}`),
        name,
        labelKey: tk(`investment.data.taxRules.fields.${name}`),
        fieldType: type,
        value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        ...(options ? { options } : {}),
        ...(required
            ? {
                validation: {
                    rules: [{ ruleId: 'required', messageKey: tk('validation.required') }],
                    validateWhen: 'submit',
                },
            }
            : {}),
        layout: { md: type === 'textarea' ? 12 : 6 },
    };
}
function withRules(base, rules) {
    return {
        ...base,
        validation: {
            ...base.validation,
            rules: [...(base.validation?.rules ?? []), ...rules],
            validateWhen: base.validation?.validateWhen ?? 'submit',
        },
    };
}
const saveAction = {
    ...metadata('action.taxRule.create'),
    id: 'action.taxRule.create',
    operationId: OPERATION_IDS.TAX_RULE_CREATE_VERSION,
    labelKey: tk('action.save'),
    iconKey: 'save',
    kind: 'command',
    commandId: 'cmd.admin.tax-rules.create-version',
    submitBehavior: 'form_submit',
    submitConfig: {
        validatesForm: true,
        requiresDirty: true,
        requiresValid: true,
        resetBehavior: 'toServerValues',
    },
    intent: 'primary',
    idempotencyRequired: true,
    successBehavior: 'NAVIGATE_BACK',
    successMessageKey: tk('common.saveSuccess'),
    errorBehavior: 'SHOW_INLINE_ERROR',
    errorMessageKey: tk('common.error.unexpected'),
    order: 10,
    group: 'primary',
};
function lifecycleAction(name, operationId, _status, intent) {
    const actionId = `action.taxRule.${name}`;
    return {
        ...metadata(actionId),
        id: actionId,
        operationId,
        labelKey: tk(`action.${name}`),
        iconKey: name,
        kind: 'command',
        commandId: `cmd.admin.tax-rules.${name}`,
        submitBehavior: 'standalone',
        intent,
        idempotencyRequired: true,
        successBehavior: 'REFRESH_TABLE',
        successMessageKey: tk('common.saveSuccess'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common.error.unexpected'),
        // P0-2: Status + maker-checker eligibility is server-authoritative via
        // row.actionEligibility. No client-side status visibilityCondition.
        order: name === 'submit' ? 10 : name === 'approve' ? 20 : name === 'suspend' ? 30 : 40,
        group: intent === 'danger' ? 'danger' : 'primary',
    };
}
const jurisdictionField = {
    ...field('jurisdiction', 'hidden'),
    value: {
        defaultValue: 'NP',
        hiddenValuePolicy: 'preserve',
        submitBehavior: 'include',
    },
};
const ruleTypeField = {
    ...field('ruleType', 'hidden'),
    value: {
        defaultValue: 'CAPITAL_GAINS',
        hiddenValuePolicy: 'preserve',
        submitBehavior: 'include',
        retainWhenHidden: true,
    },
};
const holdingPeriodMinimumDaysField = withRules(field('holdingPeriodMinimumDays', 'number', true), [{ ruleId: 'min', params: { min: 0 }, messageKey: tk('validation.min') }]);
const capitalGainsRateField = {
    ...withRules(field('capitalGainsTaxRate', 'percentage', true), [
        { ruleId: 'min', params: { min: 0 }, messageKey: tk('validation.min') },
        { ruleId: 'max', params: { max: 1 }, messageKey: tk('validation.max') },
    ]),
    helpTextKey: tk('investment.data.taxRules.help.ratePercent'),
};
const withholdingRateField = {
    ...withRules(field('withholdingTaxRate', 'percentage', true), [
        { ruleId: 'min', params: { min: 0 }, messageKey: tk('validation.min') },
        { ruleId: 'max', params: { max: 1 }, messageKey: tk('validation.max') },
    ]),
    helpTextKey: tk('investment.data.taxRules.help.ratePercent'),
};
const ROUNDING_POLICIES = [
    ['ROUND_HALF_UP_2DP', 'investment.data.taxRules.roundingOptions.halfUp2'],
    ['ROUND_HALF_EVEN_2DP', 'investment.data.taxRules.roundingOptions.halfEven2'],
    ['ROUND_DOWN_2DP', 'investment.data.taxRules.roundingOptions.down2'],
    ['ROUND_UP_2DP', 'investment.data.taxRules.roundingOptions.up2'],
    ['ROUND_HALF_UP_0DP', 'investment.data.taxRules.roundingOptions.halfUp0'],
    ['ROUND_HALF_EVEN_0DP', 'investment.data.taxRules.roundingOptions.halfEven0'],
];
const roundingPolicyField = {
    ...field('roundingPolicy', 'select', false, ROUNDING_POLICIES.map(([value, labelKey]) => ({ value, labelKey: tk(labelKey) }))),
    value: {
        defaultValue: 'ROUND_HALF_UP_2DP',
        hiddenValuePolicy: 'unregister',
        submitBehavior: 'include',
    },
    helpTextKey: tk('investment.data.taxRules.help.roundingPolicy'),
};
export const TAX_RULE_CREATE_FORM = {
    ...metadata('form.admin.taxRule.create'),
    id: 'form.admin.taxRule.create',
    titleKey: tk('investment.data.taxRules.actions.create'),
    descriptionKey: tk('investment.data.taxRules.description'),
    mode: 'create',
    fields: [
        jurisdictionField,
        ruleTypeField,
        field('holdingPeriodClassification', 'select', true, [
            {
                value: 'SHORT_TERM',
                labelKey: tk('investment.data.taxRules.holdingPeriodOptions.shortTerm'),
            },
            {
                value: 'LONG_TERM',
                labelKey: tk('investment.data.taxRules.holdingPeriodOptions.longTerm'),
            },
        ]),
        holdingPeriodMinimumDaysField,
        field('effectiveFrom', 'date', true),
        field('effectiveTo', 'date'),
        capitalGainsRateField,
        withholdingRateField,
        roundingPolicyField,
        field('sourceReference', 'textarea'),
    ],
    sections: [
        {
            id: 'section.taxRule.scope',
            titleKey: tk('investment.data.taxRules.section.scope'),
            fields: [
                'field.taxRule.jurisdiction',
                'field.taxRule.ruleType',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
        {
            id: 'section.taxRule.holdingPeriod',
            titleKey: tk('investment.data.taxRules.section.holdingPeriod'),
            fields: [
                'field.taxRule.holdingPeriodClassification',
                'field.taxRule.holdingPeriodMinimumDays',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
        {
            id: 'section.taxRule.rates',
            titleKey: tk('investment.data.taxRules.section.rates'),
            fields: [
                'field.taxRule.capitalGainsTaxRate',
                'field.taxRule.withholdingTaxRate',
                'field.taxRule.roundingPolicy',
            ].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
        {
            id: 'section.taxRule.effectiveDates',
            titleKey: tk('investment.data.taxRules.section.effectiveDates'),
            fields: ['field.taxRule.effectiveFrom', 'field.taxRule.effectiveTo'].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
        {
            id: 'section.taxRule.reference',
            titleKey: tk('investment.data.taxRules.section.reference'),
            fields: ['field.taxRule.sourceReference'].map(fieldId),
            layout: { columns: 1, gap: 16 },
        },
    ],
    actions: [saveAction],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: 'cmd.admin.tax-rules.create-version',
        successMessageKey: tk('common.saveSuccess'),
        errorMessageKey: tk('common.error.unexpected'),
    },
};
function column(name, overrides = {}) {
    return {
        ...metadata(`column.taxRule.${name}`),
        labelKey: tk(`investment.data.taxRules.fields.${name}`),
        field: name,
        fieldType: 'text',
        sortable: false,
        filterable: false,
        searchable: false,
        groupable: false,
        hideable: true,
        defaultVisible: true,
        requiredVisible: false,
        resizable: true,
        reorderable: true,
        pinnable: false,
        defaultWidth: 150,
        minWidth: 90,
        maxWidth: 320,
        align: 'left',
        nullDisplayBehavior: 'dash',
        ...overrides,
    };
}
export const TAX_RULE_TABLE = {
    ...metadata('table.admin.taxRules'),
    id: 'table.admin.taxRules',
    titleKey: tk('investment.data.taxRules.title'),
    dataSourceId: 'query.admin.tax-rules.list',
    rowIdField: 'id',
    columns: [
        column('jurisdiction', {
            requiredVisible: true,
            sortable: true,
            filterable: true,
            searchable: true,
            allowedFilterOperators: ['equals'],
        }),
        column('ruleType', { defaultVisible: false }),
        column('holdingPeriodClassification'),
        column('effectiveFrom', { fieldType: 'date', sortable: true }),
        column('effectiveTo', { fieldType: 'date' }),
        column('capitalGainsTaxRate', { fieldType: 'percentage', align: 'right', requiredVisible: true }),
        column('withholdingTaxRate', { fieldType: 'percentage', align: 'right', requiredVisible: true }),
        column('status', {
            fieldType: 'status',
            requiredVisible: true,
            sortable: true,
            filterable: true,
            allowedFilterOperators: ['equals'],
        }),
        column('version', { fieldType: 'number', sortable: true }),
        column('sourceReference', { defaultVisible: false }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
    defaultSort: { field: 'effectiveFrom', direction: 'desc' },
    rowActions: [
        lifecycleAction('submit', OPERATION_IDS.TAX_RULE_SUBMIT, 'DRAFT', 'primary'),
        lifecycleAction('approve', OPERATION_IDS.TAX_RULE_APPROVE, 'PENDING_APPROVAL', 'primary'),
        lifecycleAction('suspend', OPERATION_IDS.TAX_RULE_SUSPEND, 'ACTIVE', 'warning'),
        lifecycleAction('retire', OPERATION_IDS.TAX_RULE_RETIRE, 'SUSPENDED', 'danger'),
    ],
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    pageActions: [
        {
            ...metadata('action.taxRules.import'),
            id: 'action.taxRules.import',
            operationId: OPERATION_IDS.IMPORT_UPLOAD,
            labelKey: tk('action.import'),
            iconKey: 'upload',
            kind: 'navigate',
            intent: 'secondary',
            idempotencyRequired: false,
            successBehavior: 'NAVIGATE_TO',
            targetRouteId: ROUTE_IDS.DATA_IMPORT_UPLOAD,
            successNavigateTo: `${getRouteById(ROUTE_IDS.DATA_IMPORT_UPLOAD)?.path ?? '/data/imports/upload'}?importType=${encodeURIComponent('TAX_RULE')}`,
            errorBehavior: 'SHOW_INLINE_ERROR',
            order: 20,
            group: 'secondary',
        },
    ],
    capabilities: {
        sorting: true,
        filtering: true,
        globalSearch: true,
        pagination: true,
        columnVisibility: true,
        columnResizing: true,
        columnReordering: true,
        export: true,
        refresh: true,
        density: true,
        create: true,
    },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: true,
    },
};
